import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { MemberOrder } from '../../auth/memberApi';
import OrderCards from './OrderCards';

const CustomerOrders = () => {
  const { createOrderInvoiceDownload, listOrders } = useAuth();
  const [orders, setOrders] = useState<MemberOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [invoiceBusyOrderId, setInvoiceBusyOrderId] = useState('');

  const downloadInvoice = async (order: MemberOrder) => {
    setError('');
    setInvoiceBusyOrderId(order.orderId);
    try {
      const download = await createOrderInvoiceDownload(order.orderId);
      const link = document.createElement('a');
      link.href = download.downloadUrl;
      link.target = '_blank';
      link.rel = 'noreferrer';
      link.click();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to download invoice.');
    } finally {
      setInvoiceBusyOrderId('');
    }
  };

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      setOrders(await listOrders());
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load orders.');
    } finally {
      setIsLoading(false);
    }
  }, [listOrders]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  return (
    <section className="customer-orders-page">
      <div className="customer-orders-header">
        <p>Member orders</p>
        <h1>Your Hogcloud orders.</h1>
        <span>Review purchase dates, totals, order status, items, and fulfillment estimates.</span>
      </div>

      <div className="customer-orders-toolbar" aria-label="Order tools">
        <div>
          <strong>Order history</strong>
          <span>{isLoading ? 'Loading purchases…' : `${orders.length} order${orders.length === 1 ? '' : 's'}`}</span>
        </div>
        <button type="button" onClick={loadOrders} disabled={isLoading}>
          {isLoading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <p className="customer-orders-error" role="alert">{error}</p>}

      <div aria-busy={isLoading}>
        {!isLoading && !error && (
          <OrderCards
            orders={orders}
            onDownloadInvoice={downloadInvoice}
            invoiceBusyOrderId={invoiceBusyOrderId}
          />
        )}
      </div>
    </section>
  );
};

export default CustomerOrders;
